import { AxiosError } from 'axios';
import { useGet } from 'hooks/use-get';
import React, { useContext, useEffect, useMemo } from 'react';
import { UseQueryResult } from '@tanstack/react-query';
import { useAccountState } from 'contexts/account/account-context';
import { getSites } from 'views/order-feed/api/get-sites';
import { getSiteOverview } from 'views/order-feed/features/my-supply-plan/api/get-sites';
import { SiteOverview } from 'views/order-feed/features/my-supply-plan/types/ofd-sites.type';
import { Site } from 'views/order-feed/types/site.type';

type SitesProviderProps = {
  children: React.ReactNode;
};

type State = {
  sitesQuery: UseQueryResult<Site[], AxiosError<unknown, any>>;
  siteOverviewQuery: UseQueryResult<SiteOverview[], AxiosError<unknown, any>>;
};

const SitesStateContext = React.createContext<State | undefined>(undefined);

// site provider for sites used within order feed
const SitesProvider: React.FC<SitesProviderProps> = ({ children }) => {
  const {
    account: { company },
  } = useAccountState();

  const sitesQuery = useGet(['getSites', company?.customerErpId], (context) => getSites(String(context.queryKey[1])), {
    enabled: !!company?.customerErpId && (company.features.hasOrderRead || company.features.hasOrderFeed),
  });

  const activeSiteIds = useMemo(() => {
    if (!sitesQuery.data?.length) return [];
    return sitesQuery.data.filter((s) => s.hasRecentOrders).map((s) => s.globalSiteId);
  }, [sitesQuery.data]);

  const siteOverviewQuery = useGet(
    ['getSiteOverview', company?.companyId, activeSiteIds],
    ({ queryKey }) => getSiteOverview(String(queryKey[1]), activeSiteIds),
    {
      enabled: false,
    }
  );

  useEffect(() => {
    // Wait for sitesQuery to settle
    if (company?.features.hasOrderFeed && !!company?.companyId && !!activeSiteIds.length) {
      siteOverviewQuery.refetch();
    }
  }, [sitesQuery.data, company, activeSiteIds]);

  return (
    <SitesStateContext.Provider
      value={{
        sitesQuery,
        siteOverviewQuery,
      }}>
      {children}
    </SitesStateContext.Provider>
  );
};

function useSitesState() {
  const context = useContext(SitesStateContext);
  if (context === undefined) {
    throw new Error('useSitesState must be used within a AccountProvider');
  }
  return context;
}

export { SitesProvider, useSitesState };
